import { useQuery } from "react-query";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const useMemberships = (accessToken, page, pageSize) => {
  return useQuery(["memberships", accessToken, page, pageSize], async () => {
    try {
      const response = await fetch(
        BACKEND_URL +
        `/api/v1/account/memberships?page=${page}&pageSize=${pageSize}`,
        {
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + accessToken,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch memberships!");
      }
      if (response.status >= 299) {
        throw new Error("Erreur de récupération de la liste de mes écoles!");
      }
      const result = await response.json();
      return result;
    } catch (error) {
      // Handle the error
      console.error("Error fetching memberships:", error);
      throw error;
    }
  });
};

const GetMemberships = async (accessToken, page, pageSize) => {
  try {
    const response = await fetch(BACKEND_URL +
      `/api/v1/account/memberships?page=${page}&pageSize=${pageSize}`, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + accessToken,
      },
    });
    if (!response.ok) {
      throw new Error(`Erreur de récupération de la liste de mes écoles!`);
    }
    let actualData = await response.json();
    return actualData;
  } catch (err) {
    throw err;
  }
};

const UpdateCurrentAccountPreferences = async (
  accessToken,
  lastVisitedSchool
) => {
  try {
    if (!lastVisitedSchool) {
      return null;
    }
    const headers = {
      "Content-Type": "application/json",
    };
    if (accessToken) {
      headers.Authorization = "Bearer " + accessToken;
    }
    const response = await fetch(BACKEND_URL + `/api/v1/account/preferences`, {
      headers,
      method: "PATCH",
      credentials: "include",
      body: JSON.stringify({
        last_visited_school: lastVisitedSchool ? lastVisitedSchool : null,
      }),
    });
    if (!response.ok) {
      throw new Error("Failed to update account preferences!");
    }
    if (response.status >= 299) {
      throw new Error(
        "Erreur de mise à jours des préférences de l'utilisateur courant!"
      );
    }
    const trainingObj = await response.json();
    return trainingObj?.data;
  } catch (error) {
    console.error(
      `There was an error in updating current account preferences: ${error.message}`
    );
    return null;
  }
};

const GetCurrentAccountFamily = async (schoolId) => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/v1/account/family?school_id=${schoolId}`, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });
    if (!response.ok) {
      throw new Error(`Erreur de chargement des données familiales!`);
    }
    let actualData = await response.json();
    return actualData;
  } catch (err) {
    throw err;
  }
};

const GetMemberPreferences = async (accessToken, memberId) => {
  try {
    const headers = {
      "Content-Type": "application/json",
    };
    if (accessToken) {
      headers["Authorization"] = "Bearer " + accessToken;
    }
    const response = await fetch(
      BACKEND_URL + `/api/v1/account/memberships/${memberId}/preferences`,
      {
        method: "GET",
        credentials: "include",
        headers: headers,
      }
    );
    if (!response.ok) {
      throw new Error(`Erreur de récupération des préférences du membre!`);
    }
    const result = await response.json();
    return result?.data;
  } catch (err) {
    throw err;
  }
};

const UpdateMemberPreferences = async (accessToken, memberId, lastVisitedTraining) => {
  try {
    const headers = {
      "Content-Type": "application/json",
    };
    if (accessToken) {
      headers["Authorization"] = "Bearer " + accessToken;
    }
    const response = await fetch(
      BACKEND_URL + `/api/v1/account/memberships/${memberId}/preferences`,
      {
        method: "PATCH",
        credentials: "include",
        headers: headers,
        body: JSON.stringify({
          last_visited_training: lastVisitedTraining ? lastVisitedTraining : null,
        }),
      }
    );
    if (!response.ok) {
      throw new Error("Failed to update member preferences!");
    }
    const result = await response.json();
    return result?.data;
  } catch (error) {
    console.error(`Error updating member preferences: ${error.message}`);
    throw error;
  }
};

const adminNotificationsRequest = async (accessToken, memberId, method, body) => {
  const headers = {
    "Content-Type": "application/json",
  };
  if (accessToken) {
    headers["Authorization"] = "Bearer " + accessToken;
  }
  const response = await fetch(
    BACKEND_URL + `/api/v1/account/memberships/${memberId}/admin-notifications`,
    {
      method,
      credentials: "include",
      headers,
      body: body ? JSON.stringify(body) : undefined,
    }
  );
  const result = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(
      result?.error ||
        "Erreur lors de la gestion des préférences de notifications !"
    );
  }
  return result?.data || [];
};

const GetAdminNotificationPreferences = (accessToken, memberId) =>
  adminNotificationsRequest(accessToken, memberId, "GET");

const UpdateAdminNotificationPreferences = (accessToken, memberId, notifications) =>
  adminNotificationsRequest(accessToken, memberId, "PATCH", { notifications });

const accountsAPI = {
  GetAdminNotificationPreferences,
  UpdateAdminNotificationPreferences,
  useMemberships,
  GetMemberships,
  UpdateCurrentAccountPreferences,
  GetCurrentAccountFamily,
  GetMemberPreferences,
  UpdateMemberPreferences,
};

export default accountsAPI;
